<template>
  <div class="featured-products critical-component-clear">
    <div class="featured-products__container container">
      <p
        v-if="section.title"
        class="
          featured-products__title
          text-uc-heading-3xl
          text-uc-heading-7xl-desktop
        "
        v-text="section.title"
      />

      <div
        v-if="blocks.length"
        class="featured-products__header"
      >
        <div class="featured-products__tabs">
          <template
            v-for="(block, index) of blocks"
            :key="`${block.id}-${block.title}`"
          >
            <button
              v-if="block.title"
              type="button"
              class="
                featured-products__tab-title
                text-heading-xl
                text-heading-2xl-desktop
              "
              :class="{ [$class('active')]: index === activeBlock }"
              @click="setActiveBlock(index)"
              v-text="block.title"
            />
          </template>
        </div>

        <btn
          v-if="blocks[activeBlock]?.ctaText && blocks[activeBlock]?.ctaUrl"
          class="is-hidden--mobile"
          :label="blocks[activeBlock]?.ctaText"
          :href="blocks[activeBlock]?.ctaUrl"
          custom-element="a"
          modifiers="primary"
        />
      </div>

      <div class="featured-products__carousel-container">
        <div
          ref="carousel"
          class="featured-products__carousel"
          :class="[{ 'is-dragging': dragActive }]"
          @mousedown="(event) => handleMouseDown(event)"
          @mousemove="(event) => handleMouseMove(event)"
          @mouseup="handleMouseExit"
          @mouseleave="handleMouseExit"
          @scroll="handleCarouselScroll"
        >
          <div
            v-for="product of blocks[activeBlock]?.collection"
            :key="`slide-${product.id}`"
            class="featured-products__slide"
          >
            <product-card
              :product="product"
              :show-secondary-image="blocks[activeBlock]?.showRolloverImage"
              :sizes="sizes"
            />
          </div>
        </div>

        <div
          class="
            featured-products__carousel__progress-bar-container
            container
          "
        >
          <progress
            class="featured-products__progress-bar"
            :class="{
              [$class('hidden')]:
                (isMobile && blocks[activeBlock]?.collection.length <= 1) ||
                (!isMobile && blocks[activeBlock]?.collection.length <= 3),
            }"
            :value="carouselProgress"
            max="100"
          />
        </div>
      </div>

      <!-- Mobile CTA -->
      <div
        v-if="blocks[activeBlock]?.ctaText && blocks[activeBlock]?.ctaUrl"
        class="featured-products__cta--mobile is-hidden--desktop"
      >
        <btn
          :label="blocks[activeBlock]?.ctaText"
          :href="blocks[activeBlock]?.ctaUrl"
          custom-element="a"
          modifiers="primary"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Featured products (featured-products)
 * -----------------------------------------------------------------------------
 * Section with tabs containing a product carousel.
 *
 * @param {Object} section - Featured Products section settings
 * @param {Object} blocks - Featured Products block settings
 *
 */
import { mapState } from 'vuex'
import { values as breakpoints } from '~/config/breakpoints'

import Btn from '~global/btn/btn'
import ProductCard from '~async/product-card/product-card'

export default {
  name: 'FeaturedProducts',

  components: {
    Btn,
    ProductCard,
  },

  props: {
    section: {
      type: Object,
      default: () => ({
        title: '',
      }),
    },
    blocks: {
      type: Object,
      default: () => ({
        id: '',
        title: '',
        cta_text: '',
        cta_url: '',
        collection: [],
        showRolloverImage: '',
      }),
    },
  },

  data() {
    return {
      activeBlock: 0,
      carouselProgress: 0,
      dragActive: false,
      dragTimer: '',
      scrollLeft: 0,
      startX: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.l}) 33vw, 100vw`
    },
  },

  watch: {

    /**
     * Watch active block to reset carousel to start.
     */
    activeBlock() {
      const carousel = this.$refs.carousel

      if (!carousel) {
        return
      }

      carousel.scrollLeft = 0
      this.scrollLeft = 0
    },
  },

  mounted() {
    this.handleCarouselScroll()
  },

  methods: {

    /**
     * Set active block in carousel
     * @param {Number} index - Block index.
     */
    setActiveBlock(index) {
      this.activeBlock = index
    },

    /**
     * Handles mouse down event for featured products carousel
     * @param {Object} event - Mouse event.
     */
    handleMouseDown(event) {
      if (this.isMobile) {
        return
      }

      const carousel = this.$refs.carousel

      /**
       * Use setTimeout to differentiate between click and drag
       */
      this.dragTimer = setTimeout(() => {
        this.dragActive = true
        this.startX = event.pageX - carousel.offsetLeft
        this.scrollLeft = carousel.scrollLeft
      }, 150)
    },

    /**
     * Handles mouse move event for featured products carousel
     * - Allows carousel to be draggable
     * @param {Object} event - Mouse event.
     */
    handleMouseMove(event) {
      if (this.isMobile || !this.dragActive) {
        return
      }

      const carousel = this.$refs.carousel
      const scrollOffset = event.pageX - carousel.offsetLeft
      const scroll = (scrollOffset - this.startX) * 1.5
      carousel.scrollLeft = this.scrollLeft - scroll
    },

    /**
     * Handles mouse leave or mouse up event for featured products carousel
     */
    handleMouseExit() {
      clearTimeout(this.dragTimer)
      this.dragActive = false
    },

    /**
     * Handles carousel scroll event to update progress bar
     */
    handleCarouselScroll() {
      const carousel = this.$refs.carousel

      if (!carousel) {
        return
      }

      const scrollWidth = carousel.scrollWidth
      const width = carousel.offsetWidth
      const scroll = carousel.scrollLeft

      const progress = ((scroll + width) / scrollWidth) * 100

      this.carouselProgress = progress
    },
  },
}
</script>

<style lang="scss">
@import './featured-products';
</style>
